import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Building2,
  AlertTriangle,
  FileText,
  Wrench,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-300 hover:shadow-sm transition-all duration-200 group"
    >
      <div className="flex items-start justify-between mb-6">
        <div
          className="w-10 h-10 flex items-center justify-center rounded-sm"
          style={{ backgroundColor: `${accent}15` }}
        >
          <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={1.5} />
        </div>
        <ArrowUpRight
          className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 transition-colors"
          strokeWidth={1.5}
        />
      </div>
      <div className="text-4xl font-chivo font-black text-neutral-950 tracking-tight leading-none mb-2">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
        {label}
      </div>
    </div>
  );
}

function PriorityBar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-manrope font-medium text-neutral-700">{label}</span>
        <span className="text-sm font-chivo font-bold text-neutral-950">{count}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => console.error("Failed to load dashboard stats:", e))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !stats) {
    return (
      <div className="text-neutral-500 font-manrope text-sm" data-testid="dashboard-loading">
        A carregar estatísticas…
      </div>
    );
  }

  const totalPri = Object.values(stats.ocorrencias_por_prioridade || {}).reduce(
    (a, b) => a + b,
    0
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          testid="stat-condominios"
          label="Condomínios"
          value={stats.total_condominios}
          icon={Building2}
          accent="#0047FF"
        />
        <StatCard
          testid="stat-servicos"
          label="Serviços agendados"
          value={stats.servicos_agendados}
          icon={Wrench}
          accent="#F59E0B"
        />
      </div>

      {/* Priority breakdown */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold mb-6">
          Ocorrências por prioridade
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <PriorityBar label="Urgente" count={stats.ocorrencias_por_prioridade.Urgente || 0} total={totalPri} color="#FF4500" />
          <PriorityBar label="Alta" count={stats.ocorrencias_por_prioridade.Alta || 0} total={totalPri} color="#F59E0B" />
          <PriorityBar label="Média" count={stats.ocorrencias_por_prioridade["Média"] || 0} total={totalPri} color="#0047FF" />
          <PriorityBar label="Baixa" count={stats.ocorrencias_por_prioridade.Baixa || 0} total={totalPri} color="#71717A" />
        </div>
      </div>

      {/* Recent occurrences */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold mb-1">
              Última atividade
            </div>
            <h3 className="text-lg font-chivo font-bold text-neutral-950">Ocorrências recentes</h3>
          </div>
          <Link
            to="/registros?tab=servicos"
            data-testid="dashboard-goto-ocorrencias"
            className="text-xs font-manrope font-semibold text-neutral-900 border border-zinc-200 hover:bg-zinc-50 px-4 py-2 rounded-sm transition-colors flex items-center gap-2"
          >
            Ver registros
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        </div>
        {stats.recentes_ocorrencias.length === 0 ? (
          <div className="p-10 text-center text-sm font-manrope text-neutral-500">
            Sem ocorrências registadas.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <th className="py-3 px-6 text-left">Título</th>
                <th className="py-3 px-6 text-left">Condomínio</th>
                <th className="py-3 px-6 text-left">Prioridade</th>
                <th className="py-3 px-6 text-left">Estado</th>
                <th className="py-3 px-6 text-left">Data</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentes_ocorrencias.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors"
                >
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-900 font-medium">
                    {o.titulo}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600">
                    {o.condominio_nome}
                  </td>
                  <td className="py-4 px-6">
                    <PriorityChip p={o.prioridade} />
                  </td>
                  <td className="py-4 px-6">
                    <StateChip s={o.estado} />
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600">{o.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PriorityChip({ p }) {
  const map = {
    Urgente: "bg-[#FF4500]/10 text-[#FF4500] border-[#FF4500]/20",
    Alta: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30",
    "Média": "bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/20",
    Baixa: "bg-zinc-100 text-neutral-600 border-zinc-200",
  };
  return (
    <span
      className={`inline-block text-[10px] font-chivo font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${map[p] || map.Baixa}`}
    >
      {p}
    </span>
  );
}

function StateChip({ s }) {
  const map = {
    Aberta: "bg-[#FF4500]/10 text-[#FF4500] border-[#FF4500]/20",
    "Em Análise": "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30",
    Resolvida: "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30",
    Fechada: "bg-zinc-100 text-neutral-600 border-zinc-200",
  };
  return (
    <span
      className={`inline-block text-[10px] font-chivo font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${map[s] || map.Fechada}`}
    >
      {s}
    </span>
  );
}
